import matter from 'gray-matter';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  date: string;
  category: string;
  categorySlug: string;
  excerpt: string;
  readTime: string;
  content: string;
  image?: string;
  audio?: string;
}

// Import all markdown files from the blog directory
const blogFiles = import.meta.glob('../content/blog/*.md', { 
  eager: true,
  query: '?raw'
});

/**
 * Parse all blog posts from imported markdown files
 */
function parseBlogPosts(): BlogPost[] {
  const posts: BlogPost[] = [];
  
  if (!blogFiles || Object.keys(blogFiles).length === 0) {
    return [];
  }
  
  for (const [filepath, module] of Object.entries(blogFiles)) {
    // The module should have a default export with the raw content
    const content = (module as any).default || module;
    
    if (typeof content !== 'string') {
      continue;
    }
    
    // Extract filename from path
    const filename = filepath.split('/').pop() || '';
    
    // Parse the frontmatter and content
    const { data, content: markdownContent } = matter(content);
    
    posts.push({
      id: data.id || filename.replace('.md', ''),
      title: data.title || 'Untitled',
      slug: data.slug || filename.replace('.md', ''),
      date: data.date || new Date().toLocaleDateString(),
      category: data.category || 'Uncategorized',
      categorySlug: data.categorySlug || 'uncategorized',
      excerpt: data.excerpt || '',
      readTime: data.readTime || '5 min read',
      content: markdownContent,
      image: data.image,
      audio: data.audio,
    });
  }
  
  // Sort posts by date (newest first)
  return posts.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
}

// Parse all posts once and cache them
const allPosts = parseBlogPosts();

/**
 * Get all blog posts
 */
export function getAllBlogPosts(): BlogPost[] {
  return allPosts;
}

/**
 * Get a single blog post by category and slug
 */
export function getBlogPost(categorySlug: string, slug: string): BlogPost | undefined {
  return allPosts.find(post => post.categorySlug === categorySlug && post.slug === slug);
}

/**
 * Get all blog posts for a specific category
 */
export function getBlogPostsByCategory(categorySlug: string): BlogPost[] {
  return allPosts.filter(post => post.categorySlug === categorySlug);
}

/**
 * Get recent blog posts
 */
export function getRecentBlogPosts(limit: number = 3): BlogPost[] {
  return allPosts.slice(0, limit);
}

/**
 * Get all unique categories
 */
export function getAllCategories(): Array<{ name: string; slug: string }> {
  const categoriesMap = new Map<string, { name: string; slug: string }>();
  
  allPosts.forEach(post => {
    if (!categoriesMap.has(post.categorySlug)) {
      categoriesMap.set(post.categorySlug, {
        name: post.category,
        slug: post.categorySlug,
      });
    }
  });
  
  return Array.from(categoriesMap.values());
}
